import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const SubmitPost = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [authorEmail, setAuthorEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    if (!title.trim() || !body.trim()) {
      setError('Title and body are required.');
      return;
    }
    if (!authorEmail.trim()) {
      setError('Email is required. Only invited authors can submit.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/api/blog', {
        title: title.trim(),
        body: body.trim(),
        authorName: authorName.trim() || undefined,
        authorEmail: authorEmail.trim() || undefined
      });
      setMessage(
        data.post?.status === 'published'
          ? 'Thank you! Your post has been published.'
          : 'Thank you! Your post has been submitted and will be reviewed by our team.'
      );
      setTitle('');
      setBody('');
      setAuthorName('');
      setAuthorEmail('');
      setTimeout(() => navigate('/blog'), 2000);
    } catch (err) {
      setError(err.response?.status === 403
        ? (err.response?.data?.error || 'Only invited authors can submit.')
        : (err.response?.data?.error || 'Failed to submit. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <Link to="/blog" className="text-blue-600 hover:text-blue-800 text-sm font-medium mb-4 inline-block">← Back to Blog</Link>
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Submit a post</h1>

        {message && (
          <div className="mb-4 p-4 bg-green-50 text-green-800 rounded-lg">{message}</div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Post title"
              required
            />
          </div>
          <div>
            <label htmlFor="body" className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
            <textarea
              id="body"
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={8}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Write your post..."
              required
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="authorName" className="block text-sm font-medium text-gray-700 mb-1">Your name</label>
              <input
                id="authorName"
                type="text"
                value={authorName}
                onChange={e => setAuthorName(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Anonymous"
              />
            </div>
            <div>
              <label htmlFor="authorEmail" className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                id="authorEmail"
                type="email"
                value={authorEmail}
                onChange={e => setAuthorEmail(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="you@example.com"
                required
              />
              <p className="mt-1 text-xs text-gray-500">Only invited authors can submit.</p>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Submit for review'}
            </button>
            <Link to="/blog" className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Cancel</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubmitPost;
